import { useEffect, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { TablePagination } from '@/components/ui/pagination'
import { SearchInput } from '@/components/ui/search-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDebouncedInput } from '@/hooks/useDebouncedInput'
import {
  type AgentRunParams,
  RunStatus,
  useAgentRunFilterOptions,
  useAgentRunList,
} from '@/models/agentRunReport'
import { formatCost, formatDateTime, formatLatency, formatTokens } from './format'
import { StatusBadge } from './StatusBadge'

const STATUS_LABELS: Partial<Record<RunStatus, string>> = {
  [RunStatus.SUCCESS]: 'Succeeded',
  [RunStatus.ERROR]: 'Failed',
  [RunStatus.CANCELLED]: 'Cancelled',
}

const ANY_STATUS = '__any__'

export function RunsTab({
  params,
  onInspectRun,
}: {
  params: AgentRunParams
  onInspectRun: (runId: string) => void
}) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<RunStatus | ''>('')
  const { inputValue, setInputValue } = useDebouncedInput('', (value) => {
    setSearch(value)
    setPage(1)
  })
  const { data: options } = useAgentRunFilterOptions()
  const query = { ...params, search, status }
  const { data, isLoading } = useAgentRunList(query, page)

  // Page 5 of every run is off the end of one filtered agent's 20.
  const queryKey = JSON.stringify(query)
  useEffect(() => setPage(1), [queryKey])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={inputValue}
          onValueChange={setInputValue}
          placeholder="Search messages, errors, sessions..."
          aria-label="Search agent runs"
          className="max-w-md flex-1"
        />
        {/* Status lives here, not in the shared bar: applied before the
            aggregations it would make every error rate 100% or 0%. */}
        <Select
          value={status || ANY_STATUS}
          onValueChange={(next) => setStatus(next === ANY_STATUS ? '' : (next as RunStatus))}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_STATUS}>Any status</SelectItem>
            {(options?.statuses ?? []).map((value) => (
              <SelectItem key={value} value={value}>
                {STATUS_LABELS[value] ?? value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {isLoading || !data ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <SkeletonRows rows={8} />
        </div>
      ) : !data.results.length ? (
        <p className="text-ink-400 py-12 text-center text-sm">No runs match these filters.</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">When</TableHead>
                <TableHead className="font-semibold">Agent</TableHead>
                <TableHead className="font-semibold">Model</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="text-right font-semibold">Latency</TableHead>
                <TableHead className="text-right font-semibold">Tokens</TableHead>
                <TableHead className="text-right font-semibold">Cost</TableHead>
                <TableHead className="font-semibold">Input</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.results.map((run) => (
                <TableRow key={run.id} className="cursor-pointer" onClick={() => onInspectRun(run.id)}>
                  <TableCell className="text-ink-500 text-sm whitespace-nowrap">
                    {formatDateTime(run.dateCreated)}
                  </TableCell>
                  <TableCell className="font-mono text-sm whitespace-nowrap">{run.agentSlug}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant="outline">{run.model || '—'}</Badge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatLatency(run.latencyMs)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {run.tokens ? formatTokens(run.tokens) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatCost(run.costUsd)}</TableCell>
                  <TableCell className="text-ink-500 max-w-md truncate text-sm">
                    {run.errorPreview || run.messagePreview}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            count={data.count}
            hasNextPage={page * data.pageSize < data.count}
            hasPreviousPage={page > 1}
            onPageChange={setPage}
            itemLabel="run"
          />
        </>
      )}
    </div>
  )
}
